import { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Toggle,
} from "@nexus/ui";
import { VENDOR_CAPABILITIES, type VendorCapability } from "@nexus/contracts";
import { useSlashSearchFocus } from "../../hooks/useSlashSearchFocus";
import {
  VENDOR_SORTS,
  type VendorCatalogFilters,
  type VendorConnectionFilter,
  type VendorSort,
} from "../../lib/vendor-catalog-model";
import type { VendorPriceTier, VendorTurnaroundBucket } from "@nexus/contracts";

interface VendorsToolbarProps {
  filters: VendorCatalogFilters;
  connectedCount: number;
  recentCount: number;
  onChange: (next: VendorCatalogFilters) => void;
}

export function VendorsToolbar({
  filters,
  connectedCount,
  recentCount,
  onChange,
}: VendorsToolbarProps) {
  const { t } = useTranslation();
  const searchRef = useRef<HTMLInputElement>(null);
  useSlashSearchFocus(searchRef);

  function toggleConnection(value: VendorConnectionFilter) {
    onChange({
      ...filters,
      connection: filters.connection === value ? null : value,
    });
  }

  function toggleCapability(capability: VendorCapability) {
    const next = filters.capabilities.includes(capability)
      ? filters.capabilities.filter((item) => item !== capability)
      : [...filters.capabilities, capability];
    onChange({ ...filters, capabilities: next });
  }

  function toggleTurnaround(value: VendorTurnaroundBucket) {
    onChange({
      ...filters,
      turnaround: filters.turnaround === value ? null : value,
    });
  }

  function togglePrice(value: VendorPriceTier) {
    onChange({
      ...filters,
      priceTier: filters.priceTier === value ? null : value,
    });
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative min-w-0 flex-1">
          <Input
            ref={searchRef}
            type="search"
            iconStart="search"
            value={filters.query}
            onChange={(event) => onChange({ ...filters, query: event.target.value })}
            placeholder={t(
              "marketplace.vendors.searchPlaceholder",
              "Search partners, capabilities, packages",
            )}
            aria-label={t("marketplace.vendors.searchAria", "Search partners")}
            aria-keyshortcuts="/"
          />
          <kbd className="pointer-events-none absolute top-1/2 right-2 hidden -translate-y-1/2 rounded-sm border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline">
            /
          </kbd>
        </div>
        <Select
          value={filters.sort}
          onValueChange={(value) => onChange({ ...filters, sort: value as VendorSort })}
        >
          <SelectTrigger
            className="w-full sm:w-48"
            aria-label={t("marketplace.vendors.sortAria", "Sort partners")}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VENDOR_SORTS.map((sort) => (
              <SelectItem key={sort} value={sort}>
                {t(`marketplace.vendors.sort.${sort}`, sort)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div
        role="group"
        aria-label={t("marketplace.vendors.filtersAria", "Partner filters")}
        className="flex flex-wrap gap-1.5"
      >
        <FilterChip
          pressed={filters.connection === "connected"}
          onPressedChange={() => toggleConnection("connected")}
          label={t("marketplace.vendors.filters.connected", "Connected")}
          badge={connectedCount > 0 ? connectedCount : undefined}
        />
        <FilterChip
          pressed={filters.connection === "available"}
          onPressedChange={() => toggleConnection("available")}
          label={t("marketplace.vendors.filters.available", "Available")}
        />
        {VENDOR_CAPABILITIES.map((capability) => (
          <FilterChip
            key={capability}
            pressed={filters.capabilities.includes(capability)}
            onPressedChange={() => toggleCapability(capability)}
            label={t(`marketplace.vendors.capabilities.${capability}`, capability)}
          />
        ))}
        <FilterChip
          pressed={filters.turnaround === "fast"}
          onPressedChange={() => toggleTurnaround("fast")}
          label={t("marketplace.vendors.filters.fastTurnaround", "Fastest")}
        />
        <FilterChip
          pressed={filters.priceTier === "budget"}
          onPressedChange={() => togglePrice("budget")}
          label={t("marketplace.vendors.filters.budget", "Budget")}
        />
        <FilterChip
          pressed={filters.priceTier === "premium"}
          onPressedChange={() => togglePrice("premium")}
          label={t("marketplace.vendors.filters.premium", "Premium")}
        />
        {recentCount > 0 && (
          <span className="self-center pl-1 text-[11px] text-muted-foreground">
            {t("marketplace.vendors.recentHint", {
              count: recentCount,
              defaultValue: "{{count}} recent",
            })}
          </span>
        )}
      </div>
    </div>
  );
}

function FilterChip({
  pressed,
  onPressedChange,
  label,
  badge,
}: {
  pressed: boolean;
  onPressedChange: () => void;
  label: string;
  badge?: number;
}) {
  return (
    <Toggle
      variant="outline"
      size="sm"
      pressed={pressed}
      onPressedChange={onPressedChange}
      className="h-8 cursor-pointer gap-1.5 px-2.5 text-xs"
    >
      {label}
      {badge != null && (
        <span className="rounded-sm bg-muted px-1 font-mono text-[10px] tabular-nums text-muted-foreground">
          {badge}
        </span>
      )}
    </Toggle>
  );
}
